<template>
    <div id="app">
        <transition name="fade" mode="out-in">
            <!-- Admin Access Route - Always accessible, bypasses maintenance -->
            <div v-if="$route.path === '/mustache-admin'" class="app-page" key="mustache-admin">
                <router-view />
                <Modals />
                <Notifications />
            </div>
            <!-- Regular App Pages -->
            <AppLoader v-else-if="generalSettings === null || (authToken !== null && authUser.user === null && !authUser.loading)" key="loading" />
            <div v-else-if="showAppPage" class="app-page" :class="{ 'desktop-chat-open': generalDesktopChatOpen && !isMobileOrTablet, 'slot-game-open': slotGameOpen }" key="page">
                <!-- Regular App Pages -->
                <Header />
                <BottomNav v-if="isMobileOrTablet" />
                <Chat />
                <SupportWidget />
                <!-- Desktop only, and hidden while chat is open (otherwise FAB stacks above the panel footer and looks duplicated) -->
                <button
                    v-if="!isMobileOrTablet && !generalDesktopChatOpen && generalSidebarMobile !== 'Chat'"
                    class="live-chat-icon-btn"
                    @click="toggleChat"
                    aria-label="Open live chat"
                >
                    <svg width="26" height="26" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <circle cx="7" cy="10" r="1" fill="currentColor"/>
                        <circle cx="10" cy="10" r="1" fill="currentColor"/>
                        <circle cx="13" cy="10" r="1" fill="currentColor"/>
                        <circle cx="16.5" cy="4.5" r="1.5" fill="#22c55e" class="live-chat-dot"/>
                    </svg>
                </button>
                <!-- Desktop: no left sidebar; main menu is in header. Mobile only: show sidebar. -->
                <SidebarLeft v-if="isMobileOrTablet" v-on:collapsed-change="onSidebarCollapsed" />
                <main v-bind:style="mainInlineStyle" v-bind:class="{ 'main-background': appHasBackground === true }" ref="mainContainer">
                    <div class="main-content-wrap">
                        <div class="main-bg-image" ></div>
                        <transition name="slide-fade" mode="out-in">
                            <router-view />
                        </transition>
                    </div>
                    <Footer />
                </main>

                <Modals />
                
                <!-- Sign In Modal (for game pages) -->
                <SignInModal 
                    :show="showSignInModal" 
                    @close="showSignInModal = false"
                    @switch-to-signup="handleSignUpRequest"
                />
                
                <!-- Sign Up Modal (for game pages) -->
                <SignupModal 
                    :show="showSignupModal" 
                    @close="showSignupModal = false"
                    @switch-to-signin="handleSignInRequest"
                />
                
                <!-- Notifications - Placed last to ensure highest priority and visibility above modals -->
                <Notifications />
            </div>
            <AppMaintenance v-else key="maintenance" />
        </transition>
    </div>
</template>

<script>
    import { mapGetters, mapActions } from 'vuex';
    import AppLoader from '@/components/AppLoader';
    import AppMaintenance from '@/components/AppMaintenance';
    import Header from '@/components/Header';
    import BottomNav from '@/components/BottomNav';
    import SidebarLeft from '@/components/navbar/SidebarLeft';
    import Chat from '@/components/chat/Chat';
    import SupportWidget from '@/components/support/SupportWidget';
    import StakeSection from '@/components/StakeSection.vue';
    import Footer from '@/components/footer/Footer';
    import Modals from '@/components/modals/Modals';
    import Notifications from '@/components/notifications/Notifications';
    import SignInModal from '@/components/SignInModal.vue';
    import SignupModal from '@/components/SignupModal.vue';

    export default {
        components: {
            AppLoader,
            AppMaintenance,
            Header,
            BottomNav,
            SidebarLeft,
            Chat,
            SupportWidget,
            StakeSection,
            Footer,
            Modals,
            Notifications,
            SignInModal,
            SignupModal
        },
        data() {
            return {
                sidebarCollapsed: false,
                windowWidth: window.innerWidth,
                showSignInModal: false,
                showSignupModal: false,
                /** Once the app page has been shown, keep showing it this session so the mobile menu doesn't disappear when socket later sends maintenance. */
                appPageHasBeenVisible: false,
                lastProcessedHash: null
            };
        },
        methods: {
            ...mapActions(['socketConnectGeneral', 'generalSocketConnectionError', 'modalsSetShow', 'modalsSetData', 'generalGetBetsDataSocket', 'generalSetDesktopChatOpen', 'generalSetSidebarMobile']),
            onSidebarCollapsed(state) {
                this.sidebarCollapsed = state;
            },
            toggleChat() {
                // Check if we're on mobile (use mobile chat functionality)
                if (window.innerWidth <= 1024) {
                    this.generalSetSidebarMobile(this.generalSidebarMobile === 'Chat' ? null : 'Chat');
                } else {
                    // Desktop: toggle desktop chat using Vuex
                    this.generalSetDesktopChatOpen(!this.generalDesktopChatOpen);
                }
            },
            onResize() {
                this.windowWidth = window.innerWidth;
            },
            handleSignInRequest() {
                this.showSignupModal = false;
                this.showSignInModal = true;
            },
            handleSignUpRequest() {
                this.showSignInModal = false;
                this.showSignupModal = true;
            },
            parseHashParams() {
                const hash = window.location.hash;
                if (!hash || !hash.includes('bet=')) return null;
                
                const params = {};
                const hashParts = hash.substring(1).split('&');
                hashParts.forEach(part => {
                    const [key, value] = part.split('=');
                    if (key && value) {
                        params[key] = decodeURIComponent(value);
                    }
                });
                
                return params.game && params.bet ? params : null;
            },
            findBetById(betId) {
                if (!this.generalBets.bets) return null;
                
                // Search in all bet arrays
                const searchArrays = ['all', 'whale', 'lucky', 'my'];
                for (const arrayName of searchArrays) {
                    if (this.generalBets.bets[arrayName] && Array.isArray(this.generalBets.bets[arrayName])) {
                        const bet = this.generalBets.bets[arrayName].find(b => b._id === betId);
                        if (bet) return bet;
                    }
                }
                
                return null;
            },
            async openBetFromHash() {
                const currentHash = window.location.hash;
                console.log('🔍 Checking hash:', currentHash);
                
                // Skip if we already processed this hash
                if (this.lastProcessedHash === currentHash) {
                    console.log('⏭️ Hash already processed, skipping');
                    return;
                }
                
                const params = this.parseHashParams();
                if (!params) {
                    console.log('❌ No valid params found in hash');
                    this.lastProcessedHash = currentHash;
                    return;
                }
                
                const { game, bet: betId } = params;
                console.log('✅ Parsed params:', { game, betId });
                
                // Mark this hash as processed
                this.lastProcessedHash = currentHash;
                
                // Wait for app to be ready
                if (!this.showAppPage) {
                    await new Promise(resolve => {
                        const checkReady = setInterval(() => {
                            if (this.showAppPage) {
                                clearInterval(checkReady);
                                resolve();
                            }
                        }, 100);
                        setTimeout(() => {
                            clearInterval(checkReady);
                            resolve();
                        }, 5000);
                    });
                }
                
                // Wait for socket to be connected (with timeout)
                let attempts = 0;
                while ((!this.socketGeneral || !this.socketGeneral.connected) && attempts < 20) {
                    await new Promise(resolve => setTimeout(resolve, 200));
                    attempts++;
                }
                
                // Try to load bets if not loaded
                if (this.generalBets.bets === null && !this.generalBets.loading) {
                    this.generalGetBetsDataSocket({});
                    // Wait a bit for bets to potentially load
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
                
                // Try to find the bet in loaded bets
                let bet = this.findBetById(betId);
                
                // If bet not found, create a minimal bet object from the hash params
                // This ensures the modal opens even if bet is not in current loaded bets
                if (!bet) {
                    bet = {
                        _id: betId,
                        method: game,
                        amount: 0,
                        payout: 0,
                        multiplier: 0,
                        user: null,
                        updatedAt: new Date().toISOString()
                    };
                }
                
                // Set modal data first
                console.log('📝 Setting modal data:', { bet, gameType: game });
                this.modalsSetData({ bet, gameType: game });
                
                // Small delay to ensure data is set
                await this.$nextTick();
                await new Promise(resolve => setTimeout(resolve, 100));
                
                // Open the bet detail modal
                console.log('🚀 Opening BetDetail modal');
                this.modalsSetShow('BetDetail');
                
                // Clear hash after opening modal (but keep track that we processed it)
                setTimeout(() => {
                    const hash = window.location.hash;
                    if (hash && hash.includes('bet=')) {
                        window.history.replaceState(null, '', window.location.pathname + window.location.search);
                        // Reset lastProcessedHash so we can process new hashes
                        this.lastProcessedHash = null;
                    }
                }, 500);
            }
        },
        computed: {
            ...mapGetters(['socketGeneral', 'generalSettings', 'authToken', 'authUser', 'generalSidebarMobile', 'generalDesktopChatOpen', 'authenticated', 'generalBets', 'modalsShow']),
            /** Show app page (Header, Sidebar, main) when maintenance is off or user is admin. Treat missing maintenance config as "show app". */
            appPageVisible() {
                if (!this.generalSettings) return false;
                const maintenance = this.generalSettings.general && this.generalSettings.general.maintenance;
                const maintenanceEnabled = maintenance && maintenance.enabled === true;
                const isAdmin = this.authUser.user && this.authUser.user.rank === 'admin';
                return !maintenanceEnabled || isAdmin;
            },
            /** Once the app has been shown, keep it visible so socket updates (e.g. maintenance enabled) don't hide the mobile menu. */
            showAppPage() {
                return this.appPageVisible || this.appPageHasBeenVisible;
            },
            /** True when viewport is mobile/tablet (≤1024px). Desktop: no left sidebar, main menu in header. */
            isMobileOrTablet() {
                return this.windowWidth <= 1024;
            },
            appGetRouteName() {
                return this.$route.name;
            },
            appHasBackground() {
                let background = true;

                if(['ProfileTransactions', 'ProfileGames', 'ProfileSettings', 'UnboxOverview', 'UnboxBox', 'BattlesOverview', 'BattlesCreate', 'BattlesGame', 'CashierLimiteds'].includes(this.appGetRouteName) === true) {
                    background = false;
                }   


                return background;
            },
            /** When slot game iframe is open, content + modal must stack above footer */
            slotGameOpen() {
                return !!this.$store.getters['slots/gameSession'];
            },
            mainInlineStyle() {
                const isHomePage = this.appGetRouteName === 'Home' || this.$route.path === '/';
                const isMobileOrTablet = this.windowWidth <= 1024;
                
                // On mobile/tablet, header is at bottom, so adjust accordingly
                if(isMobileOrTablet) {
                    const topPosition = '0';
                    const bottomPosition = '80px'; // Space for bottom header
                    const heightValue = 'calc(100% - 80px)';
                    
                    if(this.windowWidth <= 1500) {
                        return {
                            left: '0',
                            width: '100%',
                            top: topPosition,
                            bottom: bottomPosition,
                            height: heightValue
                        };
                    }
                }
                
                // Desktop - when live chat is open, shift main so content fits beside chat (300px panel)
                const topPosition = '80px';
                const heightValue = 'calc(100% - 80px)';
                const chatWidth = 300;
                if (this.generalDesktopChatOpen) {
                    return {
                        left: '0',
                        marginLeft: chatWidth + 'px',
                        width: 'calc(100% - ' + chatWidth + 'px)',
                        top: topPosition,
                        height: heightValue,
                        transition: 'margin-left 0.3s ease, width 0.3s ease'
                    };
                }
                return {
                    left: '0',
                    width: '100%',
                    top: topPosition,
                    height: heightValue
                };
            }
        },
        watch: {
            appPageVisible: {
                handler(visible) {
                    if (visible) this.appPageHasBeenVisible = true;
                },
                immediate: true
            },
            '$route': {
                handler(to, from) {
                    if(this.$refs.mainContainer !== undefined) {
                        this.$nextTick(() => {
                            const mainContainer = this.$refs.mainContainer;
                            mainContainer.scrollTo({ top: 0, behavior: 'smooth' });
                        });
                    }
                    // Check for bet hash parameters on route change
                    this.$nextTick(() => {
                        setTimeout(() => {
                            this.openBetFromHash();
                        }, 300);
                    });
                }
            },
            authenticated(newVal, oldVal) {
                console.log('🔐 Auth State Changed:', {
                    from: oldVal,
                    to: newVal,
                    user: this.authUser.user ? this.authUser.user.username : 'null',
                    token: this.authToken ? 'exists' : 'null'
                });
            }
        },
        created() {
            // Set default settings immediately to prevent infinite loading if socket fails
            if(this.generalSettings === null) {
                this.generalSocketConnectionError();
            }
            // Attempt socket connection
            this.socketConnectGeneral();
            window.addEventListener('resize', this.onResize);
            
            // Listen for sign in modal requests from Header (for game pages)
            this.$root.$on('open-signin-modal-app', this.handleSignInRequest);
            // Listen for sign up modal requests from Header (for game pages)
            this.$root.$on('open-signup-modal-app', this.handleSignUpRequest);
        },
        mounted() {
            // Check for bet hash parameters on initial load
            // Wait for app to be fully initialized
            this.$nextTick(() => {
                setTimeout(() => {
                    this.openBetFromHash();
                }, 2000);
            });
            
            // Listen for hash changes (including when hash changes without route change)
            const hashChangeHandler = () => {
                // Reset last processed hash when hash changes
                this.lastProcessedHash = null;
                setTimeout(() => {
                    this.openBetFromHash();
                }, 200);
            };
            window.addEventListener('hashchange', hashChangeHandler);
            this.hashChangeHandler = hashChangeHandler;
            
            // Also check hash periodically in case hashchange doesn't fire
            this.hashCheckInterval = setInterval(() => {
                const currentHash = window.location.hash;
                if (currentHash && currentHash.includes('bet=') && this.lastProcessedHash !== currentHash) {
                    hashChangeHandler();
                }
            }, 500);
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.onResize);
            if (this.hashChangeHandler) {
                window.removeEventListener('hashchange', this.hashChangeHandler);
            }
            if (this.hashCheckInterval) {
                clearInterval(this.hashCheckInterval);
            }
            this.$root.$off('open-signin-modal-app', this.handleSignInRequest);
            this.$root.$off('open-signup-modal-app', this.handleSignUpRequest);
        }
    }
</script>

<style>
    body .app-loader.fade-leave-active {
        transition: opacity 0.5s ease;
    }

    body .app-loader.fade-leave-active {
        opacity: 0;
    }

    body .app-page {
        width: 100%;
        height: 100%;
    }

    body .app-page.fade-enter-active,
    body .app-maintenance.fade-enter-active {
        transition: opacity 0.5s ease;
    }

    body .app-page.fade-enter-from,
    body .app-maintenance.fade-enter-from {
        opacity: 0;
    }

    body .app-page main {
        height: calc(100% - 80px);
        position: fixed;
        right: 0;
        overflow-x: hidden;
        overflow-y: scroll;
        scrollbar-width: none;
        -ms-overflow-style: none;
        background: var(--bg-purple-dark);
        transition: width 0.3s ease, left 0.3s ease;
    }

    /* Wrapper so bg can stretch full content height */
    body .app-page main .main-content-wrap {
        position: relative;
        min-height: 100%;
        z-index: 0;
    }

    /* Full-height casino bg at 10% opacity – covers all games, popular slots, entire scroll */
    body .app-page main .main-bg-image {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        min-height: 100%;
        z-index: 0;
        background-image: url('~@/assets/img/bg.jpg');
        background-repeat: no-repeat;
        background-position: center;
        background-size: cover;
        opacity: 0.2;
    }

    body .app-page main .main-content-wrap > *:not(.main-bg-image) {
        position: relative;
        z-index: 1;
    }

    body .app-page main > .footer-wrapper {
        position: relative;
        z-index: 1;
    }

    /* When slot game iframe is open, slot overlay must be above header and footer */
    body .app-page.slot-game-open .header {
        z-index: 0 !important;
    }
    body .app-page.slot-game-open main {
        z-index: 5 !important;
    }
    body .app-page.slot-game-open main .main-content-wrap {
        z-index: 10;
    }
    body .app-page.slot-game-open main > .footer-wrapper {
        z-index: 0;
    }

    /* Slot game modal: do not cover header or live chat – fill main content area only */
    body .app-page.slot-game-open .game-modal-overlay {
        top: 80px !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        width: auto !important;
        height: auto !important;
    }
    body .app-page.slot-game-open.desktop-chat-open .game-modal-overlay {
        left: 300px !important;
    }
    @media only screen and (max-width: 1024px) {
        body .app-page.slot-game-open .game-modal-overlay {
            top: 0 !important;
            bottom: 80px !important;
            left: 0 !important;
            right: 0 !important;
        }
    }

    body .app-page main::-webkit-scrollbar-track {
        background-color: transparent;
    }

    body .app-page main::-webkit-scrollbar-thumb {
        background-color: transparent;
    }

    body .app-page main::-webkit-scrollbar {
        width: 0;
        height: 0;
    }

    body .app-page main .slide-fade-enter-active {
       transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
   }

    body .app-page main .slide-fade-leave-active {
       transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
   }

    body .app-page main .slide-fade-enter {
       opacity: 0;
       transform: translateY(20px);
   }

    body .app-page main .slide-fade-leave-to {
       opacity: 0;
       transform: translateY(-10px);
   }

   @media only screen and (max-width: 1500px) {

       body .app-page main {
           width: 100%;
           right: 0;
           left: 0 !important;
           top: 80px;
           height: calc(100% - 80px);
       }

   }

   @media only screen and (max-width: 1175px) {

       body .app-page main {
           height: calc(100% - 80px);
           top: 80px;
       }

   }

   /* Adjust main content for bottom header on tablets and mobile */
   @media only screen and (max-width: 1024px) {
       body .app-page main {
           top: 0 !important;
           bottom: 80px !important;
           height: calc(100% - 80px) !important;
           padding-bottom: 20px;
           width: 100% !important;
           max-width: 100vw !important;
           left: 0 !important;
           right: 0 !important;
       }
   }

   @media only screen and (max-width: 768px) {
       body .app-page main {
           padding: 0;
           top: 0 !important;
           bottom: 80px !important;
           height: calc(100% - 80px) !important;
           padding-bottom: 20px;
           width: 100% !important;
           max-width: 100vw !important;
           left: 0 !important;
           right: 0 !important;
       }
   }

   /* Fixed Live Chat Icon at Bottom Left */
   .live-chat-icon-btn {
       position: fixed !important;
       bottom: 20px !important;
       left: 20px !important;
       top: auto !important;
       right: auto !important;
       width: 56px;
       height: 56px;
       background: var(--bg-purple-dark);
       border: 2px solid rgba(255, 255, 255, 0.2);
       border-radius: 50%;
       display: flex;
       align-items: center;
       justify-content: center;
       color: #FFFFFF;
       cursor: pointer;
       transition: all 0.3s ease;
       z-index: 1000;
       box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
       margin: 0 !important;
       padding: 0 !important;
   }

   .live-chat-icon-btn:hover {
       border-color: var(--accent-yellow);
       color: var(--accent-yellow);
       transform: translateY(-2px) scale(1.05);
       box-shadow: 0 6px 20px rgba(255, 215, 0, 0.3);
   }

   .live-chat-icon-btn.chat-open {
       border-color: var(--accent-yellow);
       color: var(--accent-yellow);
       background: rgba(255, 215, 0, 0.1);
   }

   .live-chat-icon-btn svg {
       width: 26px;
       height: 26px;
   }

   .live-chat-icon-btn .live-chat-dot {
       filter: drop-shadow(0 0 2px rgba(34, 197, 94, 0.6));
   }

   /* Floating chat FAB is not rendered on mobile (v-if); keep rule as safety if markup changes */
   @media only screen and (max-width: 1024px) {
       .live-chat-icon-btn {
           display: none !important;
       }
   }

   /* Desktop: when live chat is open, shift header so it aligns with main (300px chat panel) */
   @media only screen and (min-width: 1025px) {
       .app-page.desktop-chat-open header.header {
           left: 300px !important;
           width: calc(100% - 300px) !important;
           transition: left 0.3s ease, width 0.3s ease;
       }
   }
 </style>
